"use client";

import { useEffect, useRef, useState } from "react";

interface TurnstileProps {
    siteKey: string;
    onVerify: (token: string) => void;
    onError?: (error: unknown) => void;
    onExpire?: () => void;
    theme?: "light" | "dark" | "auto";
    className?: string;
}

declare global {
    interface Window {
        turnstile?: {
            render: (
                container: string | HTMLElement,
                options: {
                    sitekey: string;
                    callback: (token: string) => void;
                    "error-callback"?: (error: unknown) => void;
                    "expired-callback"?: () => void;
                    theme?: "light" | "dark" | "auto";
                }
            ) => string;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
        };
        onTurnstileLoad?: () => void;
    }
}

export default function Turnstile({
    siteKey,
    onVerify,
    onError,
    onExpire,
    theme = "auto",
    className,
}: TurnstileProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [widgetId, setWidgetId] = useState<string | null>(null);
    const [isApiLoaded, setIsApiLoaded] = useState(false);

    useEffect(() => {
        // Check if script is already present
        if (document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]')) {
            // API zaten yuklendiyse widget render effect'i window.turnstile'i
            // dogrudan gorur; yalnizca henuz yuklenmediyse onload beklenir.
            if (!window.turnstile) {
                // Existing script might be loading asynchronously, wait for it?
                // Simpler: define global fallback if not defined
                const oldOnLoad = window.onTurnstileLoad;
                window.onTurnstileLoad = () => {
                    setIsApiLoaded(true);
                    if (oldOnLoad) oldOnLoad();
                }
            }
            return;
        }

        // Inject script
        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
        script.async = true;
        script.defer = true;

        window.onTurnstileLoad = () => {
            setIsApiLoaded(true);
        };

        document.head.appendChild(script);

        return () => {
            // Cleanup global callback if we were the standard bearer
            if (window.onTurnstileLoad) delete window.onTurnstileLoad;
        }
    }, []);

    useEffect(() => {
        if ((!isApiLoaded && !window.turnstile) || !containerRef.current || !siteKey || widgetId) return;

        if (window.turnstile) {
            const id = window.turnstile.render(containerRef.current, {
                sitekey: siteKey,
                callback: (token) => onVerify(token),
                "error-callback": onError,
                "expired-callback": onExpire,
                theme,
            });
            setWidgetId(id);
        }

        return () => {
            // Cleanup widget on unmount
            if (widgetId && window.turnstile) {
                window.turnstile.remove(widgetId);
                setWidgetId(null);
            }
        };
    }, [isApiLoaded, siteKey, theme, onVerify, onError, onExpire, widgetId]);

    return <div ref={containerRef} className={className} />;
}
